import { useRouter } from "next/router";
import { useState } from "react";

import { logger } from "@/monitoring/utils";
import { Article, ArticleLevel } from "@/types/models";
import ContentLevel from "@/types/models/ContentLevel.interface";
import clearTextSelection from "@/view/utils/clearTextSelection";

export type TCurrentLevel = Article | ArticleLevel | ContentLevel | undefined;

const useUpdateLevel = (
  levels: ContentLevel[],
): [ContentLevel | undefined, (level: ContentLevel | undefined) => void] => {
  const router = useRouter();
  const { query, pathname } = router;
  const { levelId: levelIdQueryParam } = query;

  const [currentLevel, setCurrentLevel] = useState<ContentLevel | undefined>(
    undefined,
  );

  const handleSetLevel = (level: ContentLevel | undefined) => {
    const levelId = level?.id;
    const shouldUpdateLevelState = !levelId || levelId !== currentLevel?.id;

    // If the level is changed, reset the quiz or power word activity question to 1.
    const shouldResetQuestionQueryParam =
      query["question"] && query["question"] !== "1";
    const shouldUpdateLevelIdQueryParam = levelIdQueryParam !== levelId;

    const shouldUpdateQueryParams =
      shouldResetQuestionQueryParam || shouldUpdateLevelIdQueryParam;

    if (shouldUpdateQueryParams) {
      const href = {
        pathname,
        query: {
          ...query,
          ...(shouldResetQuestionQueryParam ? { question: "1" } : {}),
          ...(shouldUpdateLevelIdQueryParam ? { levelId } : {}),
        },
      };

      router.replace(href, undefined, { shallow: true });
    }

    if (shouldUpdateLevelState) {
      // Clear any selected text and the annotations floating button in case they are visible.
      // This prevents the annotation button from persisting on screen when a level changes.
      if (!level) {
        logger.error("useUpdateLevel: level is undefined.");
      }
      clearTextSelection();
      setCurrentLevel(level || levels[0]);
    }
  };

  return [currentLevel, handleSetLevel];
};

export default useUpdateLevel;
